import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useCompany } from "@/lib/company";
import { PageHeader, LoadingState, ErrorState, EmptyState, JsonPanel } from "@/components/shared";
import { Building2 } from "lucide-react";

// Generic wired view over a company-scoped engine. Placeholder body is the raw
// payload; real per-tab views replace `render` as they are built (task 3).
export function EngineView({
  title,
  description,
  queryKey,
  queryFn,
  render,
}: {
  title: string;
  description?: string;
  queryKey: string;
  queryFn: (token: string) => Promise<unknown>;
  render?: (data: unknown) => ReactNode;
}) {
  const { activeToken, tokenLoading } = useCompany();

  const q = useQuery({
    queryKey: [queryKey, activeToken],
    queryFn: () => queryFn(activeToken!),
    enabled: !!activeToken,
  });

  return (
    <>
      <PageHeader title={title} description={description} />
      {tokenLoading ? (
        <LoadingState />
      ) : !activeToken ? (
        <EmptyState
          icon={Building2}
          title="No company yet"
          description="Add your company to load this view."
        />
      ) : q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : render ? (
        render(q.data)
      ) : (
        <JsonPanel data={q.data} />
      )}
    </>
  );
}
